import { useState } from "react";
import { Loader2 } from "lucide-react";
import {
  AuthApiError,
  requestOtp,
  verifyOtp,
} from "../lib/platform-auth";
import {
  DEV_LOGIN_ACCOUNTS,
  type DevLoginAccount,
} from "../lib/dev-login-accounts";
import { isDevLoginEnabled } from "../lib/dev-login-enabled";
import { isPersonalEnvironment } from "../lib/environment-preference";

const LEGAL_ENTITY_BLOCKED_FA =
  "ورود شخص حقوقی به اپلیکیشن خواننده مجاز نیست. لطفاً با شماره موبایل (شخص حقیقی) وارد شوید.";

const PERSONAL_REQUIRED_FA =
  "برای ورود به اپلیکیشن خواننده فقط حساب شخص حقیقی مجاز است. محیط شخصی برای این شماره یافت نشد.";

function toWesternDigits(value: string): string {
  return value
    .replace(/[۰-۹]/g, (d) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(d)))
    .replace(/[٠-٩]/g, (d) => String("٠١٢٣٤٥٦٧٨٩".indexOf(d)));
}

type Props = {
  onSuccess?: () => void;
  showDevAccounts?: boolean;
};

export function LoginForm({ onSuccess, showDevAccounts = isDevLoginEnabled }: Props) {
  const [phone, setPhone] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [devBusyId, setDevBusyId] = useState<string | null>(null);

  function afterLogin() {
    onSuccess?.();
  }

  /** Always finish OTP in the personal workspace — no org/environment picker. */
  async function completeOtpAsPersonal(
    phoneNorm: string,
    code: string,
  ): Promise<void> {
    const verified = await verifyOtp({ phone: phoneNorm, code });
    if (!("needsOrganizationPick" in verified && verified.needsOrganizationPick)) {
      afterLogin();
      return;
    }
    const personal = verified.organizations.find((o) => isPersonalEnvironment(o));
    if (!personal) {
      throw new AuthApiError(PERSONAL_REQUIRED_FA, 403);
    }
    await verifyOtp({
      phone: phoneNorm,
      code,
      organizationId: personal.id,
    });
    afterLogin();
  }

  async function handleRequestOtp(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await requestOtp(toWesternDigits(phone));
      setOtpSent(true);
    } catch (err) {
      setError(err instanceof AuthApiError ? err.message : "ارسال کد ناموفق بود");
    } finally {
      setBusy(false);
    }
  }

  async function handleVerifyOtp(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await completeOtpAsPersonal(
        toWesternDigits(phone),
        toWesternDigits(otpCode),
      );
    } catch (err) {
      setError(err instanceof AuthApiError ? err.message : "تأیید کد ناموفق بود");
    } finally {
      setBusy(false);
    }
  }

  async function handleDevAccountClick(account: DevLoginAccount) {
    if (!isDevLoginEnabled) return;
    setError(null);
    setDevBusyId(account.id);
    setBusy(true);
    try {
      if (account.kind === "email") {
        setError(LEGAL_ENTITY_BLOCKED_FA);
        return;
      }
      const phoneNorm = toWesternDigits(account.phone);
      setPhone(phoneNorm);
      const otpRes = await requestOtp(phoneNorm);
      const code = otpRes.debugCode;
      if (!code) {
        setOtpSent(true);
        setError("کد توسعه برای این شماره برنگشت؛ دستی تأیید کنید");
        return;
      }
      setOtpCode(code);
      setOtpSent(true);
      await completeOtpAsPersonal(phoneNorm, code);
    } catch (err) {
      setError(
        err instanceof AuthApiError ? err.message : "ورود سریع ناموفق بود",
      );
    } finally {
      setDevBusyId(null);
      setBusy(false);
    }
  }

  return (
    <>
      <p className="settings-hint reader-login-helper">
        ورود فقط برای شخص حقیقی با شماره موبایل است. کارتابل و اسناد ارجاع‌شده از
        سازمان‌ها بدون تعویض محیط در همین حساب شخصی در دسترس است.
      </p>

      <div className="reader-login-form">
        {!otpSent ? (
          <form onSubmit={(e) => void handleRequestOtp(e)}>
            <label htmlFor="reader-phone">شماره موبایل</label>
            <input
              id="reader-phone"
              dir="ltr"
              required
              value={phone}
              onChange={(e) => setPhone(toWesternDigits(e.target.value))}
              placeholder="09xxxxxxxxx"
              inputMode="tel"
              autoComplete="tel"
            />
            <p className="settings-hint">کد تأیید به همین شماره ارسال می‌شود.</p>
            {error && <p className="reader-login-error">{error}</p>}
            <button type="submit" className="primary-btn" disabled={busy}>
              {busy && <Loader2 size={16} className="spin" />}
              دریافت کد
            </button>
          </form>
        ) : (
          <form onSubmit={(e) => void handleVerifyOtp(e)}>
            <label htmlFor="reader-otp">کد تأیید</label>
            <input
              id="reader-otp"
              dir="ltr"
              required
              value={otpCode}
              onChange={(e) => setOtpCode(toWesternDigits(e.target.value))}
              placeholder="******"
              inputMode="numeric"
              autoComplete="one-time-code"
            />
            <p className="settings-hint">کد ۶ رقمی ارسال‌شده را وارد کنید.</p>
            {error && <p className="reader-login-error">{error}</p>}
            <button type="submit" className="primary-btn" disabled={busy}>
              {busy && <Loader2 size={16} className="spin" />}
              تأیید و ورود
            </button>
            <button
              type="button"
              className="ghost-btn"
              onClick={() => {
                setOtpSent(false);
                setOtpCode("");
                setError(null);
              }}
            >
              تغییر شماره
            </button>
          </form>
        )}
      </div>

      {showDevAccounts ? (
        <div className="reader-login-dev reader-login-dev-inline">
          <h2>حساب‌های توسعه</h2>
          <p className="settings-hint">
            فقط حساب‌های موبایل (شخص حقیقی) — ورود همیشه در محیط شخصی است.
          </p>
          {DEV_LOGIN_ACCOUNTS.filter((a) => a.kind === "otp").map((account) => (
            <button
              key={account.id}
              type="button"
              className="doc-list-item"
              disabled={busy}
              onClick={() => void handleDevAccountClick(account)}
            >
              <div className="doc-list-title">{account.orgName}</div>
              <div className="doc-list-meta">
                <span>{account.label}</span>
                <span>{account.phone}</span>
                {devBusyId === account.id ? (
                  <Loader2 size={14} className="spin" />
                ) : (
                  <span className="badge">ورود</span>
                )}
              </div>
            </button>
          ))}
        </div>
      ) : null}
    </>
  );
}
